import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { PageWrapper } from "../components";
import { 
  Moon, ChevronLeft, Lock, Check, Play, 
  Clock, Sparkles, ChevronRight 
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const IntegrationJourneyPage = () => {
  const navigate = useNavigate();
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchJourney();
  }, []);

  const fetchJourney = async () => {
    const token = localStorage.getItem("reclaim_token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/integration/journey`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || "Access denied");
      }

      const data = await response.json();
      setJourney(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStartNight = (nightNumber) => {
    navigate(`/integration/night/${nightNumber}`);
  };

  const progressPercent = journey ? (journey.completed_count / journey.total_nights) * 100 : 0;

  if (loading) {
    return (
      <PageWrapper>
        <div className="min-h-screen bg-gradient-to-b from-[#0f0f1a] to-[#1a1a2e] flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </PageWrapper>
    );
  }

  if (error) {
    return (
      <PageWrapper>
        <div className="min-h-screen bg-gradient-to-b from-[#0f0f1a] to-[#1a1a2e] p-6">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-indigo-300 mb-6"
          >
            <ChevronLeft size={20} />
            Back
          </button>
          <div className="text-center py-12">
            <Lock className="w-16 h-16 text-indigo-400/50 mx-auto mb-4" />
            <h2 className="text-xl font-heading text-white mb-2">Access Required</h2>
            <p className="text-indigo-200/60">{error}</p>
          </div>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <div className="min-h-screen bg-gradient-to-b from-[#0f0f1a] to-[#1a1a2e]">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gradient-to-b from-[#0f0f1a] to-transparent pb-4">
          <div className="p-4 flex items-center justify-between">
            <button 
              onClick={() => navigate(-1)}
              className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
            >
              <ChevronLeft size={20} className="text-white" />
            </button>
            <div className="flex items-center gap-2">
              <Moon className="w-5 h-5 text-indigo-400" />
              <span className="text-indigo-200/60 text-sm">Inner Circle</span>
            </div>
          </div>
        </div>

        {/* Journey Header */}
        <div className="px-6 pb-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 
                          flex items-center justify-center mx-auto mb-4 shadow-lg shadow-indigo-500/30">
              <Moon className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl font-heading text-white mb-2">
              {journey?.title}
            </h1>
            <p className="text-indigo-200/60 text-sm max-w-xs mx-auto">
              {journey?.subtitle}
            </p>
          </motion.div>

          {/* Progress Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white/5 rounded-2xl p-5 mb-6 border border-indigo-500/10"
          >
            <div className="flex justify-between items-center mb-3">
              <span className="text-white font-medium">Your Progress</span>
              <span className="text-indigo-400 text-sm">
                {journey?.completed_count} of {journey?.total_nights} nights
              </span>
            </div>
            <div className="h-3 bg-indigo-950/50 rounded-full overflow-hidden mb-4">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progressPercent}%` }}
                transition={{ duration: 1, ease: "easeOut" }}
                className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full"
              />
            </div>
            <div className="flex gap-1 flex-wrap">
              {journey?.nights?.map((night) => (
                <div
                  key={night.night}
                  className={`w-3 h-3 rounded-full transition-all ${
                    night.is_completed 
                      ? "bg-indigo-500" 
                      : night.is_unlocked 
                        ? "bg-indigo-400/50" 
                        : "bg-indigo-950/50"
                  }`}
                  title={`Night ${night.night}: ${night.theme}`}
                />
              ))}
            </div>
          </motion.div>

          {/* Nights List */}
          <div className="space-y-3">
            {journey?.nights?.map((night, index) => (
              <motion.div
                key={night.night}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 * index }}
              >
                <NightCard 
                  night={night} 
                  onStart={() => handleStartNight(night.night)}
                />
              </motion.div>
            ))}
          </div>
        </div>

        {/* Bottom Padding for safe area */}
        <div className="h-24" />
      </div>
    </PageWrapper>
  );
};

const NightCard = ({ night, onStart }) => {
  const isLocked = !night.is_unlocked;
  const isCompleted = night.is_completed;
  const isCurrent = night.is_unlocked && !night.is_completed;

  return (
    <div
      onClick={() => !isLocked && onStart()}
      className={`
        relative rounded-xl p-4 transition-all cursor-pointer
        ${isLocked 
          ? "bg-white/5 opacity-50 cursor-not-allowed" 
          : isCurrent
            ? "bg-gradient-to-r from-indigo-500/20 to-purple-500/20 border border-indigo-500/30 hover:border-indigo-400/50"
            : isCompleted
              ? "bg-white/5 border border-green-500/20"
              : "bg-white/5 hover:bg-white/10"
        }
      `}
    >
      <div className="flex items-center gap-4">
        {/* Night Number */}
        <div className={`
          w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0
          ${isCompleted 
            ? "bg-green-500/20" 
            : isCurrent 
              ? "bg-indigo-500/30" 
              : "bg-white/10"
          }
        `}>
          {isCompleted ? (
            <Check className="w-6 h-6 text-green-400" />
          ) : isLocked ? (
            <Lock className="w-5 h-5 text-indigo-300/50" />
          ) : (
            <span className="text-lg font-bold text-white">{night.night}</span>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className={`font-medium ${isLocked ? "text-indigo-200/50" : "text-white"}`}>
              Night {night.night}
            </h3>
            {isCurrent && (
              <span className="px-2 py-0.5 bg-indigo-500/30 rounded-full text-xs text-indigo-300">
                Current
              </span>
            )}
          </div>
          <p className={`text-sm ${isLocked ? "text-indigo-200/30" : "text-indigo-200/60"}`}>
            {night.theme}
          </p>
        </div>

        {/* Action */}
        {!isLocked && !isCompleted && (
          <div className="flex-shrink-0">
            <div className="w-10 h-10 rounded-full bg-indigo-500 flex items-center justify-center
                          shadow-lg shadow-indigo-500/30">
              <Play className="w-5 h-5 text-white ml-0.5" />
            </div>
          </div>
        )}

        {isCompleted && (
          <div className="flex-shrink-0 text-green-400">
            <Sparkles className="w-5 h-5" />
          </div>
        )}

        {isLocked && (
          <div className="flex-shrink-0">
            <Clock className="w-5 h-5 text-indigo-300/30" />
          </div>
        )}
      </div>
    </div>
  );
};

export default IntegrationJourneyPage;
